import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { style } from './zui-expander-group-css.js';

/**
 * `<zui-expander type="group-standard">` elements inserted into `<zui-expander-group type="ground-standard">` will render as standard expanders, or as the `group-standard` type.
 *
 * @element zui-expander-group
 *
 * @slot - Default, unnamed slot; for inserting `<zui-expander type="ground-standard">` elements into `<zui-expander-group type="ground-standard">`
 *
 * @event openchanged - Event is dispatched to `<zui-group-expander>` when the `open` property/attribute has been changed or the component first loads; allows `<zui-group-expander>` to regulate `<zui-expander>` elements in case consumers only want users to see one `open` expander at a time versus having multiple opened
 */
export class ZuiExpanderGroup extends ZuiBaseElement {
  /**
   * Set to `true` on `<zui-group-expander>` if you prefer ability for users to have multiple `<zui-expander>`'s open at once
   */
  @property({ type: Boolean, attribute: 'multiselect' })
  multiselect = false;

  #nodeObserver: MutationObserver | null = null;
  #expanders: HTMLCollection | undefined;

  connectedCallback() {
    super.connectedCallback();

    this._setupExpanders();
    this._setupMutationObserver();
  }

  disconnectedCallback() {
    this.#nodeObserver.disconnect();
  }

  static get styles() {
    return [super.styles, style];
  }

  render() {
    return html` <slot></slot> `;
  }

  private _setupExpanders() {
    if (this.#expanders) {
      for (const expander of this.#expanders) {
        expander.removeEventListener('openchanged', this._expanderEventListener);
      }
    }
    this.#expanders = this.getElementsByTagName('zui-expander');

    for (const expander of this.#expanders) {
      expander.addEventListener('openchanged', this._expanderEventListener.bind(this));
    }
  }

  private _setupMutationObserver() {
    this.#nodeObserver = new MutationObserver((mutationsList) => {
      for (const m of mutationsList) {
        if (m.type === 'childList') {
          this._setupExpanders();
        }
      }
    });

    this.#nodeObserver.observe(this, { childList: true });
  }

  private _expanderEventListener(event: CustomEvent) {
    if (!this.multiselect) {
      if (event.detail.open) {
        const expander = event.target;
        for (const otherExpander of this.#expanders) {
          if (otherExpander !== expander) {
            otherExpander.removeAttribute('open');
          }
        }
      }
    }
  }
}

window.customElements.define('zui-expander-group', ZuiExpanderGroup);

declare global {
  interface HTMLElementTagNameMap {
    'zui-expander-group': ZuiExpanderGroup;
  }
}
